import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import ArrowLeftIcon from "@mui/icons-material/ArrowBackIos";
import ArrowRightIcon from "@mui/icons-material/ArrowForwardIos";
import SaveIcon from "@mui/icons-material/Save";
import ReplayIcon from "@mui/icons-material/Replay";
import EjectIcon from "@mui/icons-material/Eject";
import moment from "moment-timezone";

const ALL_TIMERANGE_UNITS = {
  shift: "shifts",
  minute: "minutes",
  hour: "hours",
  day: "days",
  week: "weeks",
  month: "months",
};
const DEFAULT_TIMERANGE = 'day'
const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.text.primary,
    pointerEvents: "none !important",
  },
  track: {
    height: 6,
    bottom: 11,
    backgroundColor: "rgb(89, 89, 89)",
    pointerEvents: "none !important",
  },
  thumb: {
    backgroundColor: "white",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: theme.palette.text.primary,
    height: 16,
    width: 16,
    bottom: 6,
    pointerEvents: "auto !important",
  },
  rail: {
    opacity: 1,
  },
  mark: {
    display: "none",
  },
  markActive: {
    height: 12,
    width: 2,
    bottom: 8,
    display: "block",
    backgroundColor: theme.palette.text.hint,
  },
  placeholder: {
    width: "100%",
    height: 2,
    backgroundColor: "rgba(0, 0, 0, 0.125)",
  },
}));

const CustomDatePickerTextField = (textFieldProps) => {
  const { setOpen, ...rest } = textFieldProps;
  return <TextField {...rest} onClick={() => setOpen(true)} />;
};

const CustomDatePicker = ({
  requireTime,
  value,
  onChange,
  timezone,
  getDateFormat,
  compact,
  busy,
  readOnly,
}) => {
  const [changeValue, setChangeValue] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setChangeValue(value);
    }
  }, [open, value]);

  const DatePickerComponent = requireTime ? DateTimePicker : DatePicker;

  return (
    <DatePickerComponent
      value={changeValue}
      onChange={setChangeValue}
      onAccept={onChange}
      timezone={timezone}
      ampm={false}
      InputProps={{
        style: {
          fontSize: "0.875rem",
        },
      }}
      slots={{
        textField: CustomDatePickerTextField,
      }}
      slotProps={{
        textField: {
          variant: "standard",
          size: "small",
          readOnly: true,
          inputProps: {
            sx: {
              fieldSizing: "content",
              maxWidth: compact ? 85 : 130,
              fontSize: "0.875em",
              paddingTop: 0.625,
            },
          },
          setOpen,
        },
        actionBar: {
          actions: ["cancel", "accept"],
        },
      }}
      timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
      closeOnSelect={false}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      format={getDateFormat()}
      disableOpenPicker
      //disabled={readOnly || busy}
      disabled={busy}
      style={{
        minWidth: value ? "fit-content" : 60,
      }}
    />
  );
};

const ControlButtons = ({
  setTimeframeMoving,
  reset,
  commitChanges,
  timeframeType,
  busy,
  disabled,
  isDirty,
  readOnly,
}) => (
  <Box display="flex" flexWrap="nowrap">
    <IconButton
      size="small"
      onClick={setTimeframeMoving}
      disabled={busy || timeframeType === "moving" || disabled}
      sx={{ px: 0 }}
    >
      <EjectIcon style={{ transform: "rotate(90deg)" }} fontSize="small" />
    </IconButton>
    <IconButton
      size="small"
      onClick={reset}
      disabled={!isDirty || busy}
      sx={{ px: 0 }}
    >
      <ReplayIcon fontSize="small" />
    </IconButton>
    <IconButton
      size="small"
      onClick={commitChanges}
      disabled={readOnly || busy || !isDirty}
      sx={{ px: 0 }}
    >
      <SaveIcon fontSize="small" />
    </IconButton>
  </Box>
);

const CompactControlButtons = ({
  setTimeframeMoving,
  reset,
  commitChanges,
  timeframeType,
  busy,
  disabled,
  isDirty,
  readOnly,
  showPrevious,
  showNext,
}) => (
  <Box display="flex" flexWrap="nowrap">
    <IconButton size="small" onClick={showPrevious} disabled={disabled}>
            <ArrowLeftIcon
              fontSize="small"
              style={{ marginLeft: 4, marginRight: -4 }}
            />
          </IconButton>
    <IconButton
      size="small"
      onClick={reset}
      disabled={!isDirty || busy}
      sx={{ px: 0 }}
    >
      <ReplayIcon fontSize="small" />
    </IconButton>
              <IconButton size="small" onClick={showNext} disabled={disabled}>
            <ArrowRightIcon fontSize="small" />
          </IconButton>
  </Box>
);

const TimeRangeExplorer = (props) => {
  const [sliderValue, setSliderValue] = useState([17, 17]);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(24);

  // if dragging ignore external updates
  const isDragging = useRef(false);

  // need to know whether left or right end is being dragged
  const lastActiveThumb = useRef(null);

  // keep track between updates to control when slider is reset
  const curOptions = useRef({});

  const {
    options = {},
    setOptions,
    commit,
    rollback,
    showPrevious,
    showNext,
    adjustStartTime,
    disabled,
    busy,
    isDirty,
    readOnly,
    editPeriodsCount = false,
    useSlider = false,
    compact = false,
    mvp = true,
    timerangesSupported = [],
  } = props;

  const {
    periodsToShow: periodsToShowRaw = 0,
    timerange,
    timeframeType = "moving",
    timezone,
    startDate,
    endDate,
  } = options;

  const timerangeSelectOptions = useMemo(
    () =>
      timerangesSupported.length > 0
        ? timerangesSupported.map((tr) => [tr, ALL_TIMERANGE_UNITS[tr] ?? tr])
        : Object.entries(ALL_TIMERANGE_UNITS),
    [timerangesSupported]
  );

  const periodsToShow = parseInt(periodsToShowRaw);
  const showActiveMarks = timerange !== "hour" && timerange !== "minute";
  const requireTime =
    !timerange ||
    timerange === "shift" ||
    timerange === "hour" ||
    timerange === "minute";

  const updatePeriodsToShow = (pnum) => {
    // end is fixed, adjust start
    const newValue = [sliderValue[1] - pnum, sliderValue[1]];
    setSliderValue(newValue);
    lastActiveThumb.current = null;
    onTimeRangeChange(pnum);
  };

  const updateStartDate = (dt) => {
    const dtvalue = dt.valueOf();
    setOptions({
      timeframeType: "fixed",
      startDate: dtvalue,
    });
  };

  const getDateFormat = () => {
    let format = compact ? "MMM D" : "MMM D, YYYY";
    if (requireTime) {
      format += " HH:mm";
    }
    return format;
  };

  const getNormalizedDatetime = useCallback(
    (dt) => {
      if (Number.isNaN(dt) || dt === Infinity || dt === -Infinity) {
        return null;
      }
      return timezone ? moment(dt).tz(timezone) : moment(dt);
    },
    [timezone]
  );

  const handleSliderChange = (_, newValue, activeThumb) => {
    const [left, right] = newValue;
    const adjustedNewValue =
      activeThumb === 0
        ? [left < right ? left : right - 1, right]
        : [left, right > left ? right : right + 1];
    setSliderValue(adjustedNewValue);
    isDragging.current = true;
    lastActiveThumb.current = activeThumb;
  };

  const handleChangeCommitted = (_, newValue) => {
    const [left, right] = newValue;
    if (right >= max - 2) {
      setMax(right + 2);
    }
    if (left <= min + 2) {
      setMin(left - 2);
    }
    onTimeRangeChange(sliderValue[1] - sliderValue[0]);
    isDragging.current = false;
  };

  const onTimeRangeChange = (pnum) => {
    if (lastActiveThumb.current === 1) {
      // moved right end
      setOptions({
        timeframeType: "fixed",
        startDate,
        periodsToShow: pnum,
      });
    } else if (timeframeType === "fixed") {
      // moved left end, fixed time frame; means updating start time
      const dt = adjustStartTime(periodsToShow - pnum);
      setOptions({
        startDate: dt,
        periodsToShow: pnum,
      });
    } else {
      // moved left end, moving time frame so only need to update periods count
      setOptions({
        periodsToShow: pnum,
      });
    }
    lastActiveThumb.current = null;
  };

  const commitChanges = () => commit && commit();

  const resetSlider = useCallback(() => {
    const totalSteps = periodsToShow + 22;
    const ldx = 17;
    const rdx = periodsToShow + 17;
    setMin(0);
    setMax(totalSteps - 1);
    setSliderValue([ldx, rdx]);
    lastActiveThumb.current = null;
  }, [periodsToShow]);

  const reset = () => {
    resetSlider();
    rollback && rollback();
  };

  const setTimeframeMoving = () => {
    setOptions({ timeframeType: "moving" });
  };

  const getTimerangeLabel = (timerange, plural = true) => {
    const trl = plural ? `${timerange}s` : timerange;
    return trl
  }
    

  useEffect(() => {
    if (
      timeframeType !== curOptions.current.timeframeType ||
      startDate !== curOptions.current.startDate ||
      periodsToShow !== curOptions.current.periodsToShow ||
      timerange !== curOptions.current.timerange ||
      timezone !== curOptions.current.timezone
    ) {
      // console.log(
      //   "MISMATCH",
      //   {
      //     isDirty,
      //     timeframe,
      //     periodsToShow,
      //     timerange,
      //     timezone,
      //   },
      //   curRange.current
      // );

      if (!isDirty && !isDragging.current) {
        // only reset slider if no current edit in progress
        // console.log("MISMATCH UPDATE SLIDER - NOT DIRTY");
        resetSlider();
      } else {
        // update the slider in place?
      }

      curOptions.current = {
        timeframeType,
        startDate,
        periodsToShow,
        timerange,
        timezone,
      };
    }
  }, [
    periodsToShow,
    timerange,
    timezone,
    timeframeType,
    startDate,
    getNormalizedDatetime,
    isDirty,
    resetSlider,
  ]);

  const classes = useStyles(props);

  const startDateValue = getNormalizedDatetime(startDate);
  const endDateValue = getNormalizedDatetime(endDate);

  return (
    <Box>
      <Box
        mx={compact ? 1 : 1}
        display="flex"
        justifyContent="space-between"
        alignItems="start"
        // style={{ gap: 16 }}
      >
        {!disabled ? (
          <Box
            display="flex"
            flexWrap="wrap"
            flexDirection="row"
            alignItems={"center"}
            style={{ columnGap: 4 }}
          >
            {!compact && (
              <Box>
                {editPeriodsCount ? (
                  <>
                    <Typography>Viewing:</Typography>
                    <TextField
                      type="number"
                      value={periodsToShow}
                      onChange={(event) =>
                        updatePeriodsToShow(parseInt(event.target.value))
                      }
                      style={{ width: 48, textAlign: "center" }}
                      inputProps={{
                        min: 0,
                        style: { fontSize: "0.875rem", textAlign: "center" },
                      }}
                    ></TextField>
                    <Select
                      value={timerange}
                      style={{ fontSize: "0.875rem" }}
                      disabled={readOnly || busy}
                    >
                      {timerangeSelectOptions.map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography>from</Typography>
                  </>
                ) : (
                  <Typography>
                    Viewing {periodsToShow}{" "}
                    {getTimerangeLabel(timerange || DEFAULT_TIMERANGE, periodsToShow !== 1)}{" "} from 
                    {
                    //timeframeType === "fixed" ? "fixed" : "starting"
                    }
                  </Typography>
                )}
              </Box>
            )}
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              style={{ columnGap: 4 }}
            >
              <CustomDatePicker
                {...{
                  requireTime,
                  timezone,
                  getDateFormat,
                  compact,
                  busy,
                  readOnly,
                }}
                value={startDateValue}
                onChange={updateStartDate}
              />
              {timeframeType === "fixed" ? (
                <>
                  <Typography>to</Typography>
                  {endDateValue ? (
                    <Typography>
                      {endDateValue ? endDateValue.format(getDateFormat()) : ""}
                    </Typography>
                  ) : (
                    <Box
                      width={48}
                      height={2}
                      ml={1}
                      style={{ backgroundColor: "rgba(0, 0, 0, 0.25)" }}
                    ></Box>
                  )}
                </>
              ) : (
                <Typography>to present</Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box></Box>
        )}
        <CompactControlButtons
          {...{
            setTimeframeMoving,
            busy,
            timeframeType,
            disabled,
            reset,
            isDirty,
            commitChanges,
            readOnly,
            showPrevious,
            showNext
          }}
        />
      </Box>
      { !mvp && ( 
      <Box display="flex" alignItems="center" width="100%">
        <Box mx={1}>
          <IconButton size="small" onClick={showPrevious} disabled={disabled}>
            <ArrowLeftIcon
              fontSize="small"
              style={{ marginLeft: 4, marginRight: -4 }}
            />
          </IconButton>
        </Box>
        <Box
          height={24}
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
        >
          {useSlider && !disabled ? (
            <Slider
              classes={{
                root: classes.root,
                thumb: classes.thumb,
                rail: classes.rail,
                track: classes.track,
                mark: classes.mark,
                markActive: classes.markActive,
              }}
              value={sliderValue}
              step={1}
              min={min}
              max={max}
              onChange={handleSliderChange}
              onChangeCommitted={handleChangeCommitted}
              marks={showActiveMarks}
              disableSwap
            />
          ) : (
            <Box className={classes.placeholder}></Box>
          )}
        </Box>
        <Box mx={1}>
          <IconButton size="small" onClick={showNext} disabled={disabled}>
            <ArrowRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export { TimeRangeExplorer };
